import {
  assert,
  assertSome,
  Cell,
  isCustomerIri,
  isNone,
  isSome,
  Maybe,
  v2_AUTHORIZED_CUSTOMER_KEY,
  v2_useCart,
} from '@crocodille/common';
import { getToken } from 'next-auth/jwt';
import useSWR, { useSWRConfig } from 'swr';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useEffect, useRef } from 'react';
import {
  signIn as nextSignIn,
  signOut as nextSignOut,
  SignOutParams,
} from 'next-auth/react';
import nookies from 'nookies';

import * as routing from '../routing';

/**
 * Stores token if given via authorization header.
 */
export const APP_TOKEN_COOKIE_KEY = 'app_token';
const APP_TOKEN_COOKIE_OPTIONS = {
  path: '/',
  secure: true,
  sameSite: 'lax',
  httpOnly: true,
} as const;

import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextApiRequest,
  NextApiResponse,
  PreviewData,
} from 'next/types';
import { getSession } from 'next-auth/react';
import { postMessageToWebView } from './webview';
import { Session } from 'next-auth';

export interface AuthToken {
  readonly id?: `/api/v2/shop/customers/${number}`;
  readonly apiToken: string;
  readonly email?: Maybe<string>;
}

const EMPTY_HEADER = 'null';

export function destroyAppAuthToken({
  res,
}: {
  res: GetServerSidePropsContext['res'] | NextApiResponse;
}) {
  nookies.destroy({ res }, APP_TOKEN_COOKIE_KEY, APP_TOKEN_COOKIE_OPTIONS);
}

export async function getAppAuthToken(
  {
    req,
    res,
  }: {
    req?: GetServerSidePropsContext['req'] | NextApiRequest;
    res?: GetServerSidePropsContext['res'] | NextApiResponse;
  },
  storeTokenFromHeader: boolean,
) {
  if (isNone(req)) {
    return null;
  }

  const authHeader = req.headers.authorization;

  assert(!authHeader || typeof authHeader === 'string');

  if (
    authHeader &&
    (req?.headers?.['x-customer'] || req?.headers?.['x-sylius-customer'])
  ) {
    let apiToken = authHeader.replace('Bearer ', '').trim() as string | null;
    let id = (req.headers['x-customer'] ??
      req?.headers?.['x-sylius-customer']) as string | null;

    //
    // Headers may be explicitly be sent with value 'null' in order to clear them.
    // This is used by webview to clear the token when the user logs out.
    //
    if (apiToken?.length === 0 || apiToken === EMPTY_HEADER) {
      apiToken = null;
    }

    if (id?.length === 0 || id === EMPTY_HEADER) {
      id = null;
    }

    assert(
      id === null || (typeof id === 'string' && isCustomerIri(id)),
      'invalid x-customer header',
    );

    const authToken: Maybe<AuthToken> =
      apiToken && id ? { apiToken, id } : null;

    if (storeTokenFromHeader) {
      if (!authToken) {
        destroyAppAuthToken({ res: assertSome(res) });
      } else {
        nookies.set(
          { res },
          APP_TOKEN_COOKIE_KEY,
          JSON.stringify(authToken),
          APP_TOKEN_COOKIE_OPTIONS,
        );
      }
    }

    return authToken;
  }

  const cookies = nookies.get({ req });
  const tokenViaCookie = cookies[APP_TOKEN_COOKIE_KEY];

  if (tokenViaCookie) {
    try {
      return JSON.parse(tokenViaCookie);
    } catch (_) {
      return null;
    }
  }

  return null;
}

export async function getAuthToken({
  req,
  res,
}: {
  req?: GetServerSidePropsContext['req'] | NextApiRequest;
  res?: GetServerSidePropsContext['res'] | NextApiResponse;
}): Promise<Maybe<AuthToken>> {
  if (isNone(req)) {
    return null;
  }

  const appAuthToken = await getAppAuthToken({ req, res }, true);

  if (appAuthToken) {
    return appAuthToken;
  }

  const token = await getToken({
    req,
    secret: assertSome(
      process.env.NEXTAUTH_SECRET,
      'NEXTAUTH_SECRET Environment variable is required.',
    ),
  });

  if (isSome(token)) {
    const { apiToken, id } = token;

    if (!apiToken || !id) {
      return null;
    }

    const email = token.email;

    assert(typeof apiToken === 'string');
    assert(typeof id === 'string');

    return {
      id: id as AuthToken['id'],
      apiToken,
      email,
    };
  } else {
    return null;
  }
}

/**
 * Only allows page to be accessed if user is authenticated.
 *
 * Redirects to homepage if not.
 */
export function withAuth<
  P extends { [key: string]: unknown } = { [key: string]: unknown },
  Q extends ParsedUrlQuery = ParsedUrlQuery,
  D extends PreviewData = PreviewData,
>(
  getServerSideProps: GetServerSideProps<P, Q, D>,
): GetServerSideProps<P, Q, D> {
  return async function (ctx) {
    const token = await getAuthToken(ctx);

    if (isSome(token)) {
      return await getServerSideProps(ctx);
    } else {
      return {
        redirect: {
          destination: routing.localized(ctx.locale, '/'),
          permanent: false,
        },
      };
    }
  };
}

/**
 * Only allows page to be accessed if user is **not** authenticated.
 *
 * Redirects to homepage if authenticated.
 */
export function withoutAuth<
  P extends { [key: string]: unknown } = { [key: string]: unknown },
  Q extends ParsedUrlQuery = ParsedUrlQuery,
  D extends PreviewData = PreviewData,
>(
  getServerSideProps: GetServerSideProps<P, Q, D>,
): GetServerSideProps<P, Q, D> {
  return async function (ctx) {
    const token = await getAuthToken(ctx);

    if (isNone(token)) {
      return await getServerSideProps(ctx);
    } else {
      return {
        redirect: {
          destination: routing.localized(ctx.locale, '/'),
          permanent: false,
        },
      };
    }
  };
}

export const SESSION_KEY = 'nextAuthSession';

export function useAuthorizedCustomerIdCell(
  initialAuthSession: Partial<Pick<Session, 'user'>>,
): Cell<Maybe<`/api/v2/shop/customers/${number}`>> {
  const { data, isValidating } = useSWR(
    SESSION_KEY,
    async () => {
      return await getSession();
    },
    {
      fallbackData: initialAuthSession as Session,
      refreshInterval: 2 * 60 * 1000, // 2 minutes
    },
  );

  const cellRef = useRef(
    new Cell(data?.user?.id as Maybe<`/api/v2/shop/customers/${number}`>),
  );

  useEffect(() => {
    if (!isValidating) {
      cellRef.current.value = data?.user
        ?.id as Maybe<`/api/v2/shop/customers/${number}`>;
    }
  }, [data, isValidating]);

  return cellRef.current;
}

export function useAuth() {
  const { mutate } = useSWRConfig();
  const { clearCart } = v2_useCart();

  const signOut = useCallback(
    (params?: SignOutParams) => {
      // Immediately clear the customer cache so any in-flight customer.me
      // revalidations are discarded by SWR and don't restore a logged-in state
      // after the session cookie is cleared.
      mutate(v2_AUTHORIZED_CUSTOMER_KEY, null, { revalidate: false });

      return mutate(
        SESSION_KEY,
        async () => {
          try {
            await clearCart();
          } catch (e) {
            console.error('[signOut] clearCart failed:', e);
          }

          try {
            await nextSignOut(params);
          } catch (e) {
            console.error('[signOut] nextSignOut failed:', e);
            window.location.href = params?.callbackUrl ?? '/';
          }

          postMessageToWebView({ action: 'signOut' });

          return null;
        },
        { revalidate: false },
      );
    },
    [mutate, clearCart],
  );

  const signIn: typeof nextSignIn = useCallback(
    async (...params) => {
      const signInResponse = await nextSignIn(...params);
      await mutate(SESSION_KEY, undefined, { revalidate: true });

      return signInResponse;
    },
    [mutate],
  );

  return { signIn, signOut };
}
